import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Search,
  Plus, Pencil, Archive, X, Save, Loader2, Users2, ExternalLink,
  Eye, EyeOff, GraduationCap, AlertTriangle, Info, Link2,
  MoreVertical,
} from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import GroupDetailsDrawer from "./GroupDetailsDrawer";
import LimoGroupParticipants from "./LimoGroupParticipants";
import GroupSessionReminders from "./GroupSessionReminders";
import GroupSessionsEditor from "./GroupSessionsEditor";
import DatePickerField from "./DatePickerField";
import { checkSessions, hoursLabel, sameSessions, sessionLine, sessionsFromWeekly } from "../../lib/groupSessions";
import { checkKey, checkPayload } from "../../lib/groupCalendar";
import GroupCalendarPanel from "./GroupCalendarPanel";
import GroupAccessBlock from "./GroupAccessBlock";
import { accessChips, accessProblem, priceChip, pricePayload, priceProblem, primaChip } from "../../lib/groupAccess";
import { firstIssueTab, formDirty, groupSummary, shortDate, tabIssues } from "../../lib/groupForm";
import {
  CLOSE_PRESETS, GROUP_TABS, closingNote, enrolmentClosed, groupPhase, matchesSearch,
  matchesTab, periodLabel, phaseNote, readyWarnings, seatShare,
} from "../../lib/groupList";
import { monthFilters, startsInMonth } from "../../lib/groupFormat";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { isFped, regimeChip } from "../../lib/regime";
import { needsChoice } from "../../lib/initialAssessmentAdmin";
import { trainerChangeNote } from "../../lib/trainerProfile";

const FORMATS = ["online", "fizic", "hibrid"];
const STATUSES = ["active", "inactive", "full"];
const STATUS_LABELS = { active: "Activă", inactive: "Inactivă", full: "Completă" };
const DAYS = ["Luni", "Marți", "Miercuri", "Joi", "Vineri", "Sâmbătă", "Duminică"];
// Same cap as the backend (`DESCRIPTION_MAX`), so the counter and the API agree.
const GROUP_DESCRIPTION_MAX = 400;

const empty = {
  name: "",
  start_date: "",
  end_date: "",
  schedule_days: [],
  schedule_time: "",
  format: "online",
  max_seats: 25,
  status: "active",
  notes: "",
  // Public: shown on the site between the group name and its dates.
  description: "",
  // Off by default: a group that has just opened reads "1/20 înscriși" on the
  // public site, which says nobody is coming rather than that there is room.
  show_seats: false,
  // Which trainer teaches this cohort — a reference into the Traineri tab,
  // not a copy of their details. Storing name and e-mail here made one person
  // teaching three cohorts into three records at Logiscool.
  trainer_id: "",
  // Where the cohort meets: a Meet/Zoom link, or a room address for a cohort
  // that meets in person. Goes into the session reminders; never public.
  access_link: "",
  limo_account_id: "",
  session_reminders_enabled: true,
  // Opens the participants' and trainers' portal for this cohort. Off until
  // somebody turns it on: the portal starts on a pilot group (01.10.2026).
  portal_enabled: false,
  // Who can enrol and how a place is paid (07.10.2026).
  access: "public",
  billing_mode: "individual",
  institution_name: "",
  institution_contact: "",
  // The cohort's own price per person; empty = the site's price (09.10.2026).
  price: "",
  // Whether participants may pay with the Prima de Carieră Didactică card (12.10.2026).
  prima_card: true,
  // acreditat | fped (12.10.2026): Curs FPED only behind a link.
  regime: "acreditat",
  // How long before the first session the cohort leaves the site and stops
  // taking enrolments. Empty = one hour (28.09.2026).
  enrolment_close_minutes: "",
  // The exact schedule. `null` = a group still on the weekly pattern (days +
  // one time), kept as it is until an operator moves it to sessions. New
  // groups start with sessions.
  sessions: [],
};

export default function GroupsManager({ canMutate = true, currentRole }) {
  const isPartner = currentRole === "partner_operator";
  const canPublish = currentRole === "owner" || currentRole === "admin_fped" || currentRole === "admin";
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | groupId
  const [form, setForm] = useState(empty);
  // The group as it was when the form opened: who is enrolled, and which
  // sessions they were told about — so a change can be announced.
  const [original, setOriginal] = useState(null);
  const [notify, setNotify] = useState(false);
  // The group's sheet, and its tab, are in the address (03.11.2026), so a link
  // sent to a colleague opens the same sheet on the same tab.
  const [detailsId, setDetailsId] = useState(() => sheetParam("fisa_grupa"));
  const [detailsTab, setDetailsTab] = useState(() => sheetParam("fisa"));
  const openSheet = (id) => { setDetailsTab(null); setDetailsId(id); writeSheetParams(id, null); };
  // Which part of the cohort you are editing. One long scroll covered four
  // subjects at once; these are the four, minus the one nobody reads alone.
  const [tab, setTab] = useState("grupa");
  // The form as the server last received it, so the Logiscool panel can
  // tell whether it would be acting on stale data.
  const [savedForm, setSavedForm] = useState(null);
  // The formatori, fetched once: both pickers and the sentence under them
  // need the same list, and the licence one needs the chosen name.
  const [trainers, setTrainers] = useState(null);
  // M852 — visibility filter for FPED. Partners don't see this control.
  const [filterOwner, setFilterOwner] = useState("");      // "" | "fped" | "partner"
  const [filterVisible, setFilterVisible] = useState("");  // "" | "true" | "false"
  // Reading the list is a question about time first, so it opens on the
  // cohorts that need something done and narrows from there (25.09.2026).
  const [listTab, setListTab] = useState("lucru");
  const [search, setSearch] = useState("");
  const [month, setMonth] = useState("");   // "" | "2026-10"
  // "" | "acreditat" | "fped" — offered only once a Curs FPED cohort exists (13.10.2026).
  const [regimeFilter, setRegimeFilter] = useState("");
  // The archive is fetched separately and kept apart: asking the server for
  // it used to replace the working list, so every other tab counted zero
  // while it was open (25.09.2026).
  const [archivedGroups, setArchivedGroups] = useState(null);
  // The calendar rules' last answer for the open form (GroupSessionsEditor).
  const [calendarCheck, setCalendarCheck] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterOwner) params.owner_type = filterOwner;
      if (filterVisible) params.public_visible = filterVisible === "true";
      const r = await api.get("/admin/groups", { params });
      setGroups(r.data);
    } catch (e) {
      toast.error("Nu am putut încărca grupele.");
    } finally {
      setLoading(false);
    }
  }, [filterOwner, filterVisible]);

  const loadArchived = useCallback(async () => {
    try {
      const params = { archived: true };
      if (filterOwner) params.owner_type = filterOwner;
      if (filterVisible) params.public_visible = filterVisible === "true";
      const r = await api.get("/admin/groups", { params });
      setArchivedGroups(r.data || []);
    } catch (e) {
      setArchivedGroups([]);
      toast.error("Nu am putut încărca arhiva.");
    }
  }, [filterOwner, filterVisible]);

  useEffect(() => {
    load();
  }, [load]);

  // Fetched with the page, not when the tab is opened: the number on the tab
  // is the reason somebody opens it, and a blank one reads as "none".
  useEffect(() => {
    loadArchived();
  }, [loadArchived]);

  useEffect(() => {
    if (!editing) return undefined;
    const onKey = (e) => { if (e.key === "Escape") requestClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    let live = true;
    api.get("/admin/trainers")
      .then((r) => { if (live) setTrainers(r.data || []); })
      .catch(() => { if (live) setTrainers([]); });
    return () => { live = false; };
  }, []);

  const openNew = () => {
    setOriginal(null);
    setNotify(false);
    setCalendarCheck(null);
    setForm(empty);
    setSavedForm(null);
    setTab("grupa");
    setEditing("new");
  };

  const openEdit = (g) => {
    setOriginal(g);
    setNotify(false);
    setCalendarCheck(null);
    const loaded = {
      name: g.name || "",
      start_date: g.start_date || "",
      end_date: g.end_date || "",
      schedule_days: g.schedule_days || [],
      schedule_time: g.schedule_time || "",
      format: g.format || "online",
      max_seats: g.max_seats || 25,
      show_seats: Boolean(g.show_seats),
      status: g.status || "active",
      notes: g.notes || "",
      description: g.description || "",
      trainer_id: g.trainer_id || "",
      access_link: g.access_link || "",
      limo_account_id: g.limo_account_id || "",
      session_reminders_enabled: g.session_reminders_enabled !== false,
      portal_enabled: Boolean(g.portal_enabled),
      access: g.access || "public",
      billing_mode: g.billing_mode || "individual",
      institution_name: g.institution_name || "",
      institution_contact: g.institution_contact || "",
      price: g.price ?? "",
      prima_card: g.prima_card !== false,
      regime: g.regime === "fped" ? "fped" : "acreditat",
      enrolment_close_minutes: g.enrolment_close_minutes ?? "",
      sessions: g.sessions?.length
        ? g.sessions.map((x) => ({ date: x.date, start_time: x.start_time, end_time: x.end_time,
          note: x.note || "", access_link: x.access_link || "" }))
        : null,
    };
    setForm(loaded);
    setSavedForm(loaded);
    setTab("grupa");
    setEditing(g.id);
  };

  const close = () => setEditing(null);

  /**
   * Closing the form, on purpose.
   *
   * A click beside the window used to throw away everything typed, which is
   * why the backdrop no longer closes at all; the X and Escape do, and ask
   * first when there is something unsaved (24.09.2026).
   */
  const requestClose = () => {
    if (dirty && !window.confirm(
      "Ai modificări nesalvate în această grupă. Închizi fereastra și le pierzi?")) return;
    close();
  };

  const toggleDay = (d) => {
    setForm((f) => ({
      ...f,
      schedule_days: f.schedule_days.includes(d)
        ? f.schedule_days.filter((x) => x !== d)
        : [...f.schedule_days, d],
    }));
  };

  // Announcing is offered only when there is someone to tell and something new
  // to tell them.
  // What the table shows: the tab decides the time range, then the month and
  // the search narrow it. Counts come from the same rules, so a tab never
  // promises rows the filters then hide.
  const archived = archivedGroups || [];
  const source = listTab === "arhivate" ? archived : groups;
  const inTab = source.filter((g) => matchesTab(g, listTab));
  const months = monthFilters(inTab);
  const fpedInTab = inTab.filter((g) => isFped(g)).length;
  // A filter left on "Curs FPED" on a tab without any would hide everything.
  const regimeShown = fpedInTab > 0 ? regimeFilter : "";
  const visibleGroups = inTab
    .filter((g) => startsInMonth(g, month))
    .filter((g) => !regimeShown || (regimeShown === "fped") === isFped(g))
    .filter((g) => matchesSearch(g, search));
  // The archive is only loaded while its own tab is open, so its count is
  // known then and left blank otherwise rather than shown as zero.
  // Each tab counts its own list: the working ones from the live list, the
  // archive from its own, and blank until it has been fetched once.
  const tabCounts = Object.fromEntries(GROUP_TABS.map((t) => [
    t.key,
    t.key === "arhivate"
      ? (archivedGroups === null ? null : archived.length)
      : groups.filter((g) => matchesTab(g, t.key)).length,
  ]));

  const unarchive = async (g) => {
    try {
      await api.post(`/admin/groups/${g.id}/unarchive`);
      toast.success(`„${g.name}” a ieșit din arhivă. E inactivă și ascunsă de pe site.`);
      load();
      loadArchived();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut dezarhiva grupa."));
    }
  };

  const editedGroup = editing && editing !== "new" ? groups.find((g) => g.id === editing) : null;
  const summary = groupSummary(form, editedGroup);
  // The server's answer counts only while it is about what the form holds
  // now: a trainer changed on another tab makes an older "refused" stale.
  const calendarKey = checkKey(checkPayload({
    sessions: Array.isArray(form.sessions) ? form.sessions : [],
    groupId: editing && editing !== "new" ? editing : null,
    trainerId: form.trainer_id, maxSeats: form.max_seats, regime: form.regime,
  }));
  const calendarBlocking = Boolean(calendarCheck?.blocking) && calendarCheck?.key === calendarKey;
  const issues = tabIssues(form, {
    sessionsBlocking: Array.isArray(form.sessions) && (checkSessions(form.sessions).blocking || calendarBlocking),
  });

  const scheduleChanged = editing && editing !== "new" && Array.isArray(form.sessions)
    && !sameSessions(original?.sessions, form.sessions);
  const canNotify = scheduleChanged && (original?.enrolled_count || 0) > 0;

  const dirty = formDirty(form, savedForm);
  const trainerName = (trainers || []).find((t) => t.id === form.trainer_id)?.name || "";
  // The course library (28.10.2026, decision 13): no Logiscool steps for its
  // groups. A new group starts on it (decision 14).
  const usesLibrary = editing === "new" || groups.find((g) => g.id === editing)?.content_format === "library";
  const trainerNote = editing && editing !== "new"
    ? trainerChangeNote(original?.enrolled_count || 0, original?.trainer_id || "", form.trainer_id || "") : "";

  /**
   * Write the cohort. Returns the saved group, or null if it could not be.
   *
   * `silent` is the Logiscool panel saving on your behalf: same write, a
   * different sentence, and the form stays open because you were in the
   * middle of something else.
   */
  const persist = async ({ silent = false } = {}) => {
    const withSessions = Array.isArray(form.sessions);
    // A missing field may be on a tab you are not looking at, so go there
    // before saying what is wrong.
    const goTo = firstIssueTab(issues);
    if (goTo) setTab(goTo);
    if (!form.name) {
      toast.error("Numele grupei este obligatoriu.");
      return;
    }
    if (withSessions && form.sessions.length === 0) {
      toast.error("Adaugă cel puțin o sesiune.");
      return;
    }
    if (withSessions && checkSessions(form.sessions).blocking) {
      toast.error("Unele sesiuni au erori. Le vezi marcate cu roșu.");
      return;
    }
    if (calendarBlocking) {
      setTab("program");
      toast.error("Unele sesiuni nu respectă regulile de calendar. Le vezi marcate cu roșu, cu motivul.");
      return;
    }
    if (!withSessions && !form.start_date) {
      toast.error("Data de start este obligatorie.");
      return;
    }
    const accessIssue = accessProblem(form) || priceProblem(form.price);
    if (accessIssue && !isPartner) {
      toast.error(accessIssue);
      return;
    }
    try {
      const payload = {
        ...form,
        max_seats: Number(form.max_seats),
        show_seats: Boolean(form.show_seats),
        // Empty is "use the default", which the server reads as null — not 0,
        // which would mean "closes exactly at the start".
        enrolment_close_minutes: form.enrolment_close_minutes === "" || form.enrolment_close_minutes === null
          ? null : Number(form.enrolment_close_minutes),
        // 0 tells the server "back to the site's price"; an empty field must not be sent as "".
        price: pricePayload(form.price),
        prima_card: form.prima_card !== false,
      };
      if (withSessions) {
        // The server derives these from the sessions.
        ["start_date", "end_date", "schedule_days", "schedule_time"].forEach((k) => delete payload[k]);
        payload.notify_participants = Boolean(scheduleChanged && notify);
      } else {
        delete payload.sessions;
      }
      const wasNew = editing === "new";
      const r = wasNew
        ? await api.post("/admin/groups", payload)
        : await api.put(`/admin/groups/${editing}`, payload);
      toast.success(silent ? "Am salvat întâi modificările."
        : wasNew ? "Grupă creată" : "Grupă actualizată");
      if (r.data?.notified_participants) {
        toast.success(`Am trimis noul program pe email către ${r.data.notified_participants} ${
          r.data.notified_participants === 1 ? "participant" : "participanți"}.`);
      }
      // What only the server knows (legal holidays, past dates): said once,
      // after saving, since it never blocks.
      (r.data?.session_warnings || [])
        .filter((w) => !w.startsWith("Sesiunile însumează"))
        .forEach((w) => toast.warning(w));
      setSavedForm(form);
      // A cohort saved for the first time from the Logiscool tab becomes the
      // cohort being edited, so the panel below has something to act on.
      if (wasNew && r.data?.id) {
        setOriginal(r.data);
        setEditing(r.data.id);
      }
      await load();
      return r.data || { id: editing };
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
      return null;
    }
  };

  const save = async (e) => {
    e.preventDefault();
    // Read before saving: persist() moves `editing` when the cohort is new.
    const before = editing !== "new" ? groups.find((g) => g.id === editing) : null;
    const saved = await persist();
    if (!saved) return;
    // A new trainer for a cohort already at Logiscool: ask, then change it
    // there too (PR L2). Each mode the cohort exists in, apart.
    if (before && (before.trainer_id || null) !== (form.trainer_id || null)) {
      await offerTrainerSync(before);
    }
    if (before && (before.name || "") !== (form.name || "")) {
      for (const sandbox of [false, true]) {
        if (!(sandbox ? before.limo_test_group_id : before.limo_group_id)) continue;
        if (window.confirm(`${sandbox ? "[Probă] " : ""}Trimit noul nume „${form.name}” și la Logiscool?`)) {
          await renameAtLimo(before.id, sandbox);
        }
      }
    }
    close();
    load();
  };

  /** What the Logiscool buttons call first: the cohort there has to be this one. */
  const ensureSaved = async () => (dirty ? Boolean(await persist({ silent: true })) : true);

  const offerTrainerSync = async (group) => {
    for (const sandbox of [false, true]) {
      if (!(sandbox ? group.limo_test_group_id : group.limo_group_id)) continue;
      const q = `${sandbox ? "[Probă] " : ""}Grupa există la Logiscool. Schimb trainerul și acolo? `
        + "Cel vechi e scos din grupă, apoi e pus cel nou; dacă Logiscool refuză, cel vechi e pus la loc.";
      if (!window.confirm(q)) {
        toast.message("Trainerul nu s-a schimbat la Logiscool. O poți face din „Vezi ce are Logiscool”.");
        continue;
      }
      await syncTrainer(group.id, sandbox);
    }
  };

  const archive = async (id) => {
    // 19.10.2026: archiving also closes the portal for everybody in the group.
    if (!window.confirm("Arhivezi această grupă? Dispare de pe site și din fila Portal, iar cursanții și formatorul "
      + "pierd accesul la portal. Fișierele rămân la noi.")) return;
    try {
      await api.delete(`/admin/groups/${id}`);
      toast.success("Grupă arhivată");
      load();
      setArchivedGroups(null);
    } catch (err) {
      toast.error("Eroare");
    }
  };

  // M852 — owner/admin: flip public visibility for a single group.
  const togglePublish = async (g) => {
    const action = g.public_visible ? "unpublish" : "publish";
    try {
      await api.post(`/admin/groups/${g.id}/${action}`);
      toast.success(g.public_visible ? "Grupă ascunsă de pe site" : "Grupă publicată pe site");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nu am putut actualiza vizibilitatea.");
    }
  };

  const quickStatus = async (g, status) => {
    try {
      await api.put(`/admin/groups/${g.id}`, { status });
      toast.success("Status actualizat");
      load();
    } catch {
      toast.error("Eroare");
    }
  };

  return (
    <div data-testid="groups-manager">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-display text-lg font-bold text-[#373A45]">
            {isPartner ? "Grupele mele" : "Toate grupele"}
          </h2>
          <div className="text-sm text-[#6B7280] mt-0.5">
            {loading
              ? "Se încarcă…"
              : listTab === "arhivate"
                ? `${visibleGroups.length} din ${archived.length} arhivate`
                : `${visibleGroups.length} din ${groups.length} grup${groups.length === 1 ? "ă" : "e"}`}
            {isPartner && (
              <span className="ml-2 text-[11px] uppercase tracking-widest text-[#7A5A1B] font-semibold">
                · grupele tale sunt interne până când FPED le publică
              </span>
            )}
          </div>
        </div>
        <button
          onClick={openNew}
          disabled={!canMutate}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] hover:bg-[#7A4986] disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 text-sm font-semibold"
          data-testid="groups-create-button"
        >
          <Plus size={14} /> Grupă nouă
        </button>
      </div>

      {!isPartner && (
        <GroupCalendarPanel
          canEdit={currentRole === "owner"}
          refreshKey={groups}
          onOpenGroup={(id) => {
            const g = groups.find((x) => x.id === id);
            if (g) { openEdit(g); setTab("program"); }
          }}
        />
      )}

      <div className="mb-3 rounded-2xl border border-[#E5E7EB] bg-white p-3" data-testid="groups-toolbar">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative grow sm:grow-0">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9AA0A6]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="caută după nume sau notiță…"
              className="w-full rounded-full border border-[#E5E7EB] py-1.5 pl-8 pr-3 text-sm sm:w-72"
              data-testid="groups-search"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {GROUP_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => { setListTab(t.key); setMonth(""); }}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                  listTab === t.key
                    ? "bg-[#965EA7] text-white"
                    : "border border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]"
                }`}
                data-testid={`groups-tab-${t.key}`}
              >
                {t.label}
                {tabCounts[t.key] != null && (
                  <span className={`ml-1.5 tabular-nums ${listTab === t.key ? "text-white/80" : "text-[#6B7280]"}`}>
                    {tabCounts[t.key]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {months.length > 1 && (
          /* Which month a cohort starts in is how these are talked about —
             "cele din octombrie" — so it is a filter, not a sort. */
          <div className="mt-2 flex flex-wrap items-center gap-1" data-testid="groups-months">
            <span className="mr-1 text-[11px] uppercase tracking-widest text-[#6B7280]">Luna de start</span>
            <button
              type="button"
              onClick={() => setMonth("")}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                month === "" ? "bg-[#373A45] text-white" : "border border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]"
              }`}
              data-testid="groups-month-all"
            >
              Toate
            </button>
            {months.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setMonth(month === m.value ? "" : m.value)}
                className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${
                  month === m.value
                    ? "bg-[#373A45] text-white"
                    : "border border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]"
                }`}
                data-testid={`groups-month-${m.value}`}
              >
                {m.label} <span className={month === m.value ? "text-white/80" : "text-[#6B7280]"}>{m.count}</span>
              </button>
            ))}
          </div>
        )}
        {fpedInTab > 0 && (
          /* Curs FPED cohorts are worked differently — no ministry calendar,
             register or annexes — so they can be told apart at a click. */
          <div className="mt-2 flex flex-wrap items-center gap-1" data-testid="groups-regimes">
            <span className="mr-1 text-[11px] uppercase tracking-widest text-[#6B7280]">Regim</span>
            {[["", "Toate", inTab.length], ["acreditat", "Acreditat MEC", inTab.length - fpedInTab],
              ["fped", "Curs FPED", fpedInTab]].map(([value, label, count]) => (
              <button key={value || "all"} type="button" onClick={() => setRegimeFilter(value)}
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  regimeShown === value ? "bg-[#373A45] text-white" : "border border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]"}`}
                data-testid={`groups-regime-filter-${value || "all"}`}>
                {label} <span className={regimeShown === value ? "text-white/80" : "text-[#6B7280]"}>{count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* M852 — visibility/owner filters for FPED only */}
      {!isPartner && (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-3 mb-3 flex flex-wrap gap-3 text-xs">
          <label className="inline-flex items-center gap-2 text-[#373A45]">
            Creator:
            <select
              value={filterOwner}
              onChange={(e) => setFilterOwner(e.target.value)}
              className="rounded-full px-3 py-1 border border-[#E5E7EB] bg-white"
              data-testid="groups-filter-owner"
            >
              <option value="">toți</option>
              <option value="fped">FPED</option>
              <option value="partner">Parteneri</option>
            </select>
          </label>
          <label className="inline-flex items-center gap-2 text-[#373A45]">
            Vizibilitate:
            <select
              value={filterVisible}
              onChange={(e) => setFilterVisible(e.target.value)}
              className="rounded-full px-3 py-1 border border-[#E5E7EB] bg-white"
              data-testid="groups-filter-visibility"
            >
              <option value="">toate</option>
              <option value="true">Publice pe site</option>
              <option value="false">Interne</option>
            </select>
          </label>
          {(filterOwner || filterVisible) && (
            <button
              onClick={() => { setFilterOwner(""); setFilterVisible(""); }}
              className="text-[#965EA7] hover:underline"
            >
              Resetează
            </button>
          )}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <table className="w-full text-sm" data-testid="groups-table">
          <thead className="bg-[#EDEEF0] text-[#373A45]">
            <tr className="text-left">
              <Th>Grupă</Th>
              <Th>Perioadă</Th>
              <Th>Program</Th>
              <Th>Locuri</Th>
              <Th>Stare</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {!loading && visibleGroups.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-[#6B7280]" data-testid="groups-empty">
                  {groups.length === 0 && listTab === "lucru"
                    ? "Nicio grupă creată încă. Apasă „Grupă nouă” pentru a începe."
                    : "Nicio grupă aici. Schimbă fila, luna sau căutarea."}
                </td>
              </tr>
            )}
            {visibleGroups.map((g) => (
              <tr key={g.id} className="border-t border-[#E5E7EB] hover:bg-[#FBFAFC]"
                data-testid={`groups-row-${g.id}`}>
                <Td>
                  {/* The name opens the cohort: the reflex everybody had
                      before the only way in was the three-dot menu. */}
                  <button
                    type="button"
                    onClick={() => (canMutate ? openEdit(g) : openSheet(g.id))}
                    className="text-left font-semibold text-[#373A45] hover:text-[#965EA7]"
                    data-testid={`groups-open-${g.id}`}
                  >
                    {g.name}
                  </button>
                  {canPublish && needsChoice(g) && (
                    /* No choice yet for the initial assessment, and the group starts soon: nothing would be sent. */
                    <span className="ml-1.5 cursor-help text-[#B7791F]" title="Evaluarea inițială nealeasă"
                      aria-label="Evaluarea inițială nealeasă" data-testid={`groups-assessment-unset-${g.id}`}>⚠</span>
                  )}
                  {g.format && g.format !== "online" && (
                    /* Only the exception is worth a chip: everything else is online. */
                    <span className="ml-1.5 rounded-full bg-[#EDEEF0] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-[#373A45]">
                      {g.format}
                    </span>
                  )}
                  {g.notes && <div className="mt-0.5 text-[11px] text-[#6B7280]">{g.notes}</div>}
                </Td>
                <Td>
                  <div className="text-[#373A45]">{periodLabel(g)}</div>
                  <div className={`text-[11px] ${
                    groupPhase(g) === "urmeaza" ? "text-[#7A4986]" : "text-[#6B7280]"}`}
                    data-testid={`groups-phase-${g.id}`}>
                    {phaseNote(g)}
                  </div>
                </Td>
                <Td>
                  {g.sessions?.length ? (
                    <SessionsSummary group={g} />
                  ) : (
                    <>
                      <span className="text-[#373A45]">{(g.schedule_days || []).join(", ")}</span>
                      {g.schedule_time && <span className="text-[#6B7280]"> · {g.schedule_time}</span>}
                    </>
                  )}
                </Td>
                <Td>
                  <button
                    onClick={() => openSheet(g.id)}
                    className="group/seats block w-28 text-left"
                    data-testid={`groups-details-${g.id}`}
                    title="Vezi participanți"
                  >
                    <span className="inline-flex items-center gap-1 text-[#373A45] group-hover/seats:text-[#965EA7]">
                      <Users2 size={13} className="text-[#6B7280]" />
                      <span className="tabular-nums">{g.enrolled_count}/{g.max_seats}</span>
                      <ExternalLink size={10} className="text-[#965EA7]" />
                    </span>
                    <span className="mt-1 block h-1.5 w-full overflow-hidden rounded-full bg-[#EDEEF0]">
                      <span
                        className={`block h-full rounded-full ${
                          g.is_full ? "bg-[#9b1f2c]" : g.almost_full ? "bg-[#C9861A]" : "bg-[#965EA7]"}`}
                        style={{ width: `${Math.round(seatShare(g) * 100)}%` }}
                      />
                    </span>
                    {(g.is_full || g.almost_full) && (
                      <span className={`mt-0.5 block text-[10px] font-semibold uppercase tracking-widest ${
                        g.is_full ? "text-[#9b1f2c]" : "text-[#8a6a1a]"}`}>
                        {g.is_full ? "complet" : "aproape plin"}
                      </span>
                    )}
                  </button>
                </Td>
                <Td>
                  <div className="flex flex-col gap-1">
                    <span className="flex flex-wrap items-center gap-1">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
                        g.status === "active" ? "bg-[#EAF4EE] text-[#2F6B4E]"
                          : g.status === "full" ? "bg-[#FDECEC] text-[#9b1f2c]"
                            : "bg-[#EDEEF0] text-[#6B7280]"}`}
                        data-testid={`groups-status-${g.id}`}>
                        {STATUS_LABELS[g.status] || g.status}
                      </span>
                      {g.access === "link" ? (
                        accessChips(g).map((c) => (
                          <span key={c.key} data-testid={`groups-access-${c.key}-${g.id}`}
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                              c.key === "paused" ? "bg-[#FDECEC] text-[#9b1f2c]" : "bg-[#965EA7]/10 text-[#7A4986]"}`}>
                            {c.key === "link" && <Link2 size={10} />} {c.label}
                          </span>
                        ))
                      ) : g.public_visible ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1f6045]"
                          data-testid={`groups-visible-${g.id}`}>
                          <Eye size={11} /> pe site
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#7A5A1B]"
                          data-testid={`groups-visible-${g.id}`}>
                          <EyeOff size={11} /> internă
                        </span>
                      )}
                      {priceChip(g) && (
                        <span className="inline-flex rounded-full bg-[#3F8EBA]/10 px-2 py-0.5 text-[10px] font-semibold text-[#2f6d90]"
                          data-testid={`groups-price-${g.id}`}>
                          {priceChip(g)}
                        </span>
                      )}
                      {regimeChip(g) && (
                        <span className="inline-flex rounded-full bg-[#3F8EBA]/10 px-2 py-0.5 text-[10px] font-semibold text-[#2f6d90]"
                          data-testid={`groups-regime-${g.id}`}>
                          {regimeChip(g)}
                        </span>
                      )}
                      {!isFped(g) && primaChip(g) && (
                        <span className="inline-flex rounded-full bg-[#F5F6F6] px-2 py-0.5 text-[10px] font-semibold text-[#6B7280]"
                          data-testid={`groups-prima-${g.id}`}>
                          {primaChip(g)}
                        </span>
                      )}
                      {!isPartner && (
                        <span
                          className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
                            g.owner_type === "partner"
                              ? "bg-[#FDE68A]/40 text-[#7A5A1B]"
                              : "bg-[#E6F0FA] text-[#1f3a6b]"}`}
                          title={[g.partner_org, g.created_by_email].filter(Boolean).join(" · ")}
                        >
                          {g.owner_type === "partner" ? "partener" : "FPED"}
                        </span>
                      )}
                    </span>
                    {enrolmentClosed(g) && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#7A5A1B]"
                        data-testid={`groups-closed-${g.id}`} title={closingNote(g)}>
                        <EyeOff size={11} /> ieșită de pe site · înscrieri închise
                      </span>
                    )}
                    {readyWarnings(g).map((w) => (
                      /* What still stands between this cohort and its start —
                         the two that cost us this week. */
                      <span key={w} className="inline-flex items-center gap-1 text-[11px] text-[#8a6a1a]"
                        data-testid={`groups-warning-${g.id}`}>
                        <AlertTriangle size={11} /> {w}
                      </span>
                    ))}
                  </div>
                </Td>
                <Td>
                  {/*
                    M910 — actions collapsed into a single kebab dropdown for
                    a cleaner row scan. The menu is intentionally
                    forward-compatible: adding future entries (Duplică /
                    Exportă participanți / Închide înscrieri / Trimite email
                    / Marchează finalizată) only requires inserting another
                    `DropdownMenuItem` below — no row layout work needed.
                  */}
                  {(canPublish || canMutate) ? (
                    <div className="flex justify-end">
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="h-7 w-7 inline-flex items-center justify-center rounded-full hover:bg-[#F5F6F6] text-[#6B7280]"
                            data-testid={`groups-row-menu-${g.id}`}
                            aria-label="Acțiuni grupă"
                          >
                            <MoreVertical size={16} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-60">
                          {canMutate && listTab === "arhivate" && (
                            <DropdownMenuItem onClick={() => unarchive(g)}
                              data-testid={`groups-unarchive-${g.id}`}>
                              <Archive size={14} className="mr-2 text-[#2F6B4E]" />
                              Scoate din arhivă
                            </DropdownMenuItem>
                          )}
                          {canMutate && listTab !== "arhivate" && STATUSES.filter((st) => st !== g.status).map((st) => (
                            /* The status moved out of a select on every row:
                               a pill is read at a glance and cannot be changed
                               by a slip of the mouse (25.09.2026). */
                            <DropdownMenuItem key={st} onClick={() => quickStatus(g, st)}
                              data-testid={`groups-status-${st}-${g.id}`}>
                              <GraduationCap size={14} className="mr-2 text-[#6B7280]" />
                              Marchează „{STATUS_LABELS[st]}”
                            </DropdownMenuItem>
                          ))}
                          {canMutate && listTab !== "arhivate" && <DropdownMenuSeparator />}
                          {canPublish && listTab !== "arhivate" && g.access !== "link" && (
                            <DropdownMenuItem
                              onClick={() => togglePublish(g)}
                              data-testid={`groups-toggle-publish-${g.id}`}
                            >
                              {g.public_visible ? (
                                <>
                                  <EyeOff size={14} className="mr-2 text-[#7A5A1B]" />
                                  Ascunde de pe site
                                </>
                              ) : (
                                <>
                                  <Eye size={14} className="mr-2 text-[#1f6045]" />
                                  Publică pe site
                                </>
                              )}
                            </DropdownMenuItem>
                          )}
                          {canMutate && (
                            <>
                              <DropdownMenuItem
                                onClick={() => openEdit(g)}
                                data-testid={`groups-edit-${g.id}`}
                              >
                                <Pencil size={14} className="mr-2 text-[#965EA7]" />
                                Editează grupa
                              </DropdownMenuItem>
                              {listTab !== "arhivate" && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => archive(g.id)}
                                    data-testid={`groups-archive-${g.id}`}
                                  >
                                    <Archive size={14} className="mr-2 text-[#E63946]" />
                                    Arhivează grupa
                                  </DropdownMenuItem>
                                </>
                              )}
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ) : null}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-4"
          data-testid="groups-modal"
        >
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-3xl border border-[#E5E7EB] bg-white shadow-2xl"
          >
            <div className="flex shrink-0 items-center justify-between border-b border-[#E5E7EB] p-5">
              <h3 className="font-display text-lg font-bold text-[#373A45]">
                {editing === "new" ? "Grupă nouă" : "Editează grupă"}
              </h3>
              <div className="flex items-center gap-2">
                <MecRulesPopover />
                <button type="button" onClick={requestClose} className="text-[#6B7280] hover:text-[#373A45]" aria-label="Închide">
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="shrink-0 border-b border-[#E5E7EB] bg-[#FBFBFC] px-5 pt-3 pb-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#6B7280]"
                data-testid="groups-form-summary">
                {summary.map((chip, i) => (
                  <span key={chip} className="inline-flex items-center gap-2">
                    {i > 0 && <span className="text-[#C9CBD1]">·</span>}
                    {chip}
                  </span>
                ))}
              </div>
              <div className="mt-2 flex gap-1" role="tablist" aria-label="Secțiunile grupei">
                {FORM_TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.key}
                    onClick={() => setTab(t.key)}
                    className={`relative -mb-px rounded-t-lg border border-b-0 px-3 py-1.5 text-xs font-semibold ${
                      tab === t.key
                        ? "border-[#E5E7EB] bg-white text-[#373A45]"
                        : "border-transparent text-[#6B7280] hover:text-[#373A45]"
                    }`}
                    data-testid={`groups-form-tab-${t.key}`}
                  >
                    {t.label}
                    {issues[t.key].length > 0 && (
                      <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[#E63946] align-middle"
                        title={issues[t.key].join(", ")} data-testid={`groups-form-tab-dot-${t.key}`} />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto p-5">
              {tab === "grupa" && (
                <>
                  <FormRow label="Nume grupă *">
                    <input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className={inp}
                      placeholder="ex. Grupa 1 - Primăvara 2026"
                      data-testid="groups-form-name"
                    />
                  </FormRow>


                  {/* Public, unlike the internal notes further down — the label
                      says so, because the two textareas look alike and only one of
                      them is safe to write anything in. */}
                  <FormRow label="Descriere (apare pe site, sub numele grupei)">
                    <textarea
                      rows={3}
                      maxLength={GROUP_DESCRIPTION_MAX}
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className={`${inp} resize-none border-[#965EA7]/40 bg-[#FCFAFD]`}
                      placeholder="ex. Grupă desfășurată în limba maghiară"
                      data-testid="groups-form-description"
                    />
                    <div className="mt-1 flex justify-between text-[11px] text-[#6B7280]">
                      <span>Opțional. Poți folosi rânduri noi.</span>
                      <span data-testid="groups-form-description-count">
                        {(form.description || "").length}/{GROUP_DESCRIPTION_MAX}
                      </span>
                    </div>
                  </FormRow>

                  <div className="grid grid-cols-2 gap-4">
                    <FormRow label="Locuri maxime">
                      <input
                        type="number"
                        min="1"
                        max={isFped(form) ? undefined : "25"}
                        value={form.max_seats}
                        onChange={(e) => setForm({ ...form, max_seats: e.target.value })}
                        className={inp}
                        data-testid="groups-form-max-seats"
                      />
                      {!isFped(form) && Number(form.max_seats) > 25 && (
                        <p className="mt-1 text-[11px] font-semibold text-[#9b1f2c]" data-testid="groups-form-max-seats-error">
                          Cel mult 25 de formabili pe grupă (metodologia-cadru, art. 18 alin. 3).
                        </p>
                      )}
                    </FormRow>
                    <FormRow label="Status">
                      <select
                        value={form.status}
                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                        className={inp}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                        ))}
                      </select>
                    </FormRow>
                  </div>

                  {/* What the public site says about this group's availability.
                      "Grupă completă" and "Aproape plin" appear either way — the
                      switch is about the numbers, not about whether somebody can
                      tell the group is filling up. */}
                  <label
                    className="flex items-start gap-3 rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-4 py-3 cursor-pointer"
                    data-testid="group-show-seats"
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(form.show_seats)}
                      onChange={(e) => setForm({ ...form, show_seats: e.target.checked })}
                      className="mt-0.5 h-4 w-4 accent-[#965EA7]"
                    />
                    <span className="text-sm leading-relaxed text-[#373A45]">
                      <strong>Afișează public numărul de locuri</strong>
                      <span className="block text-xs text-[#6B7280] mt-0.5">
                        Când e oprit, pe site nu apare nici câte locuri sunt libere, nici
                        câți s-au înscris. „Aproape plin" și „Grupă completă" se afișează
                        în continuare.
                      </span>
                    </span>
                  </label>

                  {canPublish && !isPartner && (
                    <GroupAccessBlock form={form} setForm={setForm} enrolled={original?.enrolled_count || 0}
                      group={editing && editing !== "new" ? groups.find((x) => x.id === editing) : null}
                      onLinkChanged={(link) => setGroups((list) => list.map((x) => (
                        x.id === editing ? { ...x, enrolment_link: link } : x)))} />
                  )}

                  <FormRow label="Notițe interne (vizibile doar în admin)">
                    <textarea
                      rows={3}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className={`${inp} resize-none bg-[#F5F6F6]`}
                    />
                    <p className="mt-1 text-[11px] text-[#6B7280]">
                      Nu apar niciodată pe site — spre deosebire de descrierea de sus.
                    </p>
                  </FormRow>
                </>
              )}

              {tab === "program" && (
                <>
                  {Array.isArray(form.sessions) ? (
                    <>
                      <GroupSessionsEditor
                        sessions={form.sessions}
                        onChange={(sessions) => setForm((f) => ({ ...f, sessions }))}
                        groupId={editing && editing !== "new" ? editing : null}
                        trainerId={form.trainer_id}
                        maxSeats={form.max_seats}
                        regime={form.regime}
                        onCalendar={setCalendarCheck}
                      />
                      {canNotify && (
                        <label className="flex items-start gap-2.5 rounded-lg border border-[#F5C76E]/50 bg-[#FFF7E6] px-3 py-2.5 text-sm text-[#373A45]"
                          data-testid="groups-notify-participants">
                          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)}
                            className="mt-0.5 h-4 w-4 accent-[#965EA7]" data-testid="groups-notify-checkbox" />
                          <span>
                            <strong>Grupa are {original.enrolled_count} {original.enrolled_count === 1 ? "participant înscris" : "participanți înscriși"}.</strong>{" "}
                            Anunță-i pe email despre noul program.
                            <span className="block text-xs text-[#6B7280]">
                              Emailul pleacă celor înscriși în grupă (plătiți, confirmați sau cu înscrierea trimisă), cu lista completă a sesiunilor.
                              Fișa lor se actualizează oricum.
                            </span>
                          </span>
                        </label>
                      )}
                      <div className="grid grid-cols-2 gap-4">
                        <FormRow label="Format curs">
                          <select
                            value={form.format}
                            onChange={(e) => setForm({ ...form, format: e.target.value })}
                            className={inp}
                          >
                            {FORMATS.map((f) => (
                              <option key={f} value={f}>{f}</option>
                            ))}
                          </select>
                        </FormRow>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#965EA7]/25 bg-[#F5F0F8] px-3 py-2"
                        data-testid="groups-weekly-notice">
                        <span className="text-xs text-[#373A45]">
                          Grupa folosește încă programul general (zile + o singură oră).
                        </span>
                        <button
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, sessions: sessionsFromWeekly(f) }))}
                          className="rounded-full bg-[#965EA7] px-3 py-1 text-xs font-semibold text-white hover:bg-[#7A4986]"
                          data-testid="groups-move-to-sessions"
                        >
                          Trece la sesiuni
                        </button>
                      </div>
                    <div className="grid grid-cols-2 gap-4">
                      <FormRow label="Data start *">
                        <DatePickerField
                          value={form.start_date}
                          onChange={(v) => setForm({ ...form, start_date: v })}
                          testid="groups-form-start"
                        />
                      </FormRow>
                      <FormRow label="Data final">
                        <DatePickerField
                          value={form.end_date || ""}
                          onChange={(v) => setForm({ ...form, end_date: v })}
                          minDate={form.start_date || undefined}
                          contextDate={form.start_date || undefined}
                          testid="groups-form-end"
                        />
                      </FormRow>
                    </div>

                    <FormRow label="Zile desfășurare">
                      <div className="flex flex-wrap gap-2">
                        {DAYS.map((d) => {
                          const on = form.schedule_days.includes(d);
                          return (
                            <button
                              key={d}
                              type="button"
                              onClick={() => toggleDay(d)}
                              className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
                                on
                                  ? "bg-[#965EA7] text-white border-[#965EA7]"
                                  : "bg-white text-[#373A45] border-[#E5E7EB] hover:border-[#965EA7]/60"
                              }`}
                            >
                              {d}
                            </button>
                          );
                        })}
                      </div>
                      {form.schedule_days.includes("Duminică") && (
                        <WarningHint testid="groups-warn-sunday">
                          Conform metodologiei MEC, cursurile acreditate nu se desfășoară duminica.
                        </WarningHint>
                      )}
                    </FormRow>

                    <div className="grid grid-cols-2 gap-4">
                      <FormRow label="Interval orar">
                        <input
                          placeholder="ex. 18:00 - 20:00"
                          value={form.schedule_time || ""}
                          onChange={(e) => setForm({ ...form, schedule_time: e.target.value })}
                          className={inp}
                        />
                        <ScheduleTimeWarnings
                          timeText={form.schedule_time || ""}
                          days={form.schedule_days}
                        />
                      </FormRow>
                      <FormRow label="Format curs">
                        <select
                          value={form.format}
                          onChange={(e) => setForm({ ...form, format: e.target.value })}
                          className={inp}
                        >
                          {FORMATS.map((f) => (
                            <option key={f} value={f}>{f}</option>
                          ))}
                        </select>
                      </FormRow>
                    </div>
                    </>
                  )}

                  <FormRow label="Iese de pe site înainte de prima sesiune">
                <select
                  value={String(form.enrolment_close_minutes ?? "")}
                  onChange={(e) => setForm({ ...form, enrolment_close_minutes: e.target.value })}
                  className={inp}
                  data-testid="group-close-minutes"
                >
                  {CLOSE_PRESETS.map((o) => (
                    <option key={o.label} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-[#6B7280]">
                  Din acel moment grupa nu mai apare în orar și nici în checkout, iar
                  înscrierile la ea sunt refuzate. Grupa rămâne activă: reamintirile și
                  accesul celor deja înscriși merg mai departe.
                  {editedGroup && closingNote(editedGroup) && (
                    <span className="mt-0.5 block font-semibold text-[#373A45]">
                      {closingNote(editedGroup)}
                    </span>
                  )}
                </p>
              </FormRow>

              <FormRow label="Link de acces la sesiuni">
                    <input
                      value={form.access_link}
                      onChange={(e) => setForm({ ...form, access_link: e.target.value })}
                      placeholder="https://meet.google.com/… sau adresa sălii"
                      maxLength={500}
                      className={inp}
                      data-testid="group-access-link"
                    />
                    <p className="mt-1 text-[11px] text-[#6B7280]">
                      Pleacă în reamintiri: cu 48 de ore înainte de prima sesiune și cu o oră înainte de
                      fiecare sesiune. Nu apare niciodată pe site. Fără link nu se trimite nimic, iar
                      echipa primește o avertizare pe email.
                    </p>
                    <label className="mt-2 flex items-center gap-2 text-xs text-[#373A45] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.session_reminders_enabled}
                        onChange={(e) => setForm({ ...form, session_reminders_enabled: e.target.checked })}
                        className="h-4 w-4 accent-[#965EA7]"
                        data-testid="group-session-reminders"
                      />
                      Trimite reamintiri înainte de sesiuni
                    </label>
                  </FormRow>

                  {editing !== "new" && (
                    <GroupSessionReminders groupId={editing} canMutate={canMutate} currentRole={currentRole} />
                  )}
                </>
              )}

              {tab === "predare" && (
                <>
                  {/* One block, because the second answer depends on the first:
                      the licence is normally the one this person already holds. */}
                  <div className="rounded-xl border border-[#E5E7EB] p-4" data-testid="group-teaching-block">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#6B7280]">
                      Cine predă
                    </div>
                    <div className="mt-2 space-y-3">
                      <TrainerPicker
                        rows={trainers}
                        value={form.trainer_id}
                        onChange={(v) => setForm({ ...form, trainer_id: v })}
                      />
                      {/* The site shows the trainer on the card (16.10.2026): who enrolled saw another one. */}
                      {trainerNote && (
                        <p className="rounded-lg bg-[#FDF6E3] px-3 py-2 text-xs text-[#8a6a1a]" data-testid="group-trainer-change-note">
                          {trainerNote}
                        </p>
                      )}
                      {!usesLibrary && (
                        <AccountPicker
                          value={form.limo_account_id}
                          trainerId={form.trainer_id}
                          trainerName={trainerName}
                          onChange={(v) => setForm({ ...form, limo_account_id: v })}
                        />
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border border-[#E5E7EB] p-4" data-testid="group-portal-block">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#6B7280]">
                      Portalul cursului
                    </div>
                    <label className={`mt-2 flex items-start gap-2 text-sm text-[#373A45] ${canPublish ? "cursor-pointer" : "opacity-60"}`}>
                      <input
                        type="checkbox"
                        checked={Boolean(form.portal_enabled)}
                        disabled={!canPublish}
                        onChange={(e) => setForm({ ...form, portal_enabled: e.target.checked })}
                        className="mt-0.5 h-4 w-4 accent-[#965EA7]"
                        data-testid="group-portal-enabled"
                      />
                      <span>
                        Deschide portalul pentru această grupă
                        <span className="mt-0.5 block text-[11px] text-[#6B7280]">
                          Întâi doar formatorul grupei, ca să pregătească temele. Cursanții cu contractul semnat
                          intră după ce le trimiți invitațiile din fila Portal.
                          {!canPublish && " Doar owner-ul și adminii FPED îl pot porni."}
                        </span>
                      </span>
                    </label>
                  </div>

                  {usesLibrary ? (
                    <div className="rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] p-4 text-sm text-[#373A45]"
                      data-testid="group-limo-library">
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-[#6B7280]">Logiscool</div>
                      <p className="mt-1">
                        {editing === "new" ? "Grupele noi folosesc biblioteca cursului" : "Grupa folosește biblioteca cursului"}:
                        nu are nevoie de grupă, cont de trainer sau conturi de cursanți Logiscool. Conținutul se alege la
                        deschiderea portalului, din fila Portal.
                      </p>
                    </div>
                  ) : editing === "new" ? (
                    <div className="rounded-xl border border-dashed border-[#E5E7EB] bg-[#F5F6F6] p-4 text-sm text-[#373A45]"
                      data-testid="group-limo-needs-save">
                      Salvează grupa întâi — abia apoi o putem crea în Logiscool.
                      <button
                        type="button"
                        onClick={() => persist()}
                        className="ml-3 rounded-full bg-[#965EA7] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#7A4986]"
                        data-testid="group-limo-save-first"
                      >
                        Salvează grupa
                      </button>
                    </div>
                  ) : (
                    <LimoGroupPanel groupId={editing} group={groups.find((g) => g.id === editing)}
                      canMutate={canMutate} ensureSaved={ensureSaved} dirty={dirty} />
                  )}
                </>
              )}
            </div>

            <div className="flex shrink-0 items-center justify-between gap-2 border-t border-[#E5E7EB] bg-[#F5F6F6]/60 p-5">
              <span className="text-xs text-[#6B7280]" data-testid="groups-form-dirty">
                {dirty && editing !== "new" ? "• modificări nesalvate" : ""}
              </span>
              <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={requestClose}
                className="rounded-full px-4 py-2 text-sm border border-[#E5E7EB] hover:bg-white"
              >
                Anulează
              </button>
              <button
                type="submit"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white px-5 py-2 text-sm font-semibold"
                data-testid="groups-form-save"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Salvează
              </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {detailsId && (
        <GroupDetailsDrawer
          key={detailsId}
          groupId={detailsId}
          onClose={() => { setDetailsId(null); writeSheetParams(null, null); }}
          currentRole={currentRole}
          initialTab={detailsTab}
          onTabChange={(key) => writeSheetParams(detailsId, key)}
          onEditSchedule={canMutate ? () => {
            const g = groups.find((x) => x.id === detailsId);
            if (!g) return;
            setDetailsId(null);
            writeSheetParams(null, null);
            openEdit(g);
            setTab("program");
          } : undefined}
        />
      )}
    </div>
  );
}

function sheetParam(name) {
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}

/** Keeps `?fisa_grupa=<id>&fisa=<tab>` in step with the open sheet, without a navigation. */
function writeSheetParams(groupId, tab) {
  const url = new URL(window.location.href);
  if (groupId) url.searchParams.set("fisa_grupa", groupId); else url.searchParams.delete("fisa_grupa");
  if (groupId && tab) url.searchParams.set("fisa", tab); else url.searchParams.delete("fisa");
  window.history.replaceState(window.history.state, "", url.toString());
}

const FORM_TABS = [
  { key: "grupa", label: "Grupa" },
  { key: "program", label: "Program și sesiuni" },
  { key: "predare", label: "Cine predă și Logiscool" },
];

const inp =
  "w-full bg-white border border-[#E5E7EB] rounded-xl px-3 py-2.5 text-[#373A45] focus:ring-2 focus:ring-[#965EA7] focus:border-transparent outline-none";

function SessionsSummary({ group }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="text-left hover:text-[#965EA7]" data-testid={`groups-sessions-${group.id}`}>
          <div className="text-[#373A45]">{group.schedule_summary}</div>
          <div className="text-[11px] text-[#6B7280] underline decoration-dotted">
            {group.session_count} {group.session_count === 1 ? "sesiune" : "sesiuni"} · {hoursLabel(group.session_total_minutes || 0)}
            {group.session_warnings?.length ? " · ⚠" : ""}
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="z-[60] w-[360px] max-w-[92vw] p-3">
        <ol className="space-y-1 text-[12.5px] text-[#373A45]">
          {group.sessions.map((x, i) => (
            <li key={`${x.date}-${x.start_time}`} className="flex gap-2">
              <span className="w-5 shrink-0 text-right tabular-nums text-[#6B7280]">{i + 1}.</span>
              <span>
                {sessionLine(x)}
                {x.note && <span className="block text-[11px] font-semibold text-[#7A4986]">{x.note}</span>}
              </span>
            </li>
          ))}
        </ol>
        {group.session_warnings?.length > 0 && (
          <ul className="mt-2 space-y-1 border-t border-[#E5E7EB] pt-2 text-[11px] text-[#7A5A1B]">
            {group.session_warnings.map((w) => <li key={w}>⚠ {w}</li>)}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

function FormRow({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#373A45] uppercase tracking-widest">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const Th = ({ children }) => (
  <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 text-[#373A45] align-top ${className}`}>{children}</td>
);


/**
 * M853 — popover with MEC accreditation guidelines, opened from the modal
 * header. Click to toggle (mobile-friendly); content stays anchored to the
 * trigger and lives in a Radix Portal with a high z-index so it sits above
 * native datepickers and the modal scroll container.
 */
function MecRulesPopover() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-full border border-[#965EA7]/30 bg-[#F0E7F4] px-3 py-1 text-[11px] font-semibold text-[#7A4986] hover:bg-[#965EA7]/15 transition-colors"
          data-testid="groups-mec-rules-trigger"
          aria-label="Reguli MEC pentru planificarea grupelor"
        >
          <GraduationCap size={13} /> Reguli MEC
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        className="w-[420px] max-w-[92vw] max-h-[70vh] overflow-y-auto z-[60] p-0 border-[#E5E7EB]"
        data-testid="groups-mec-rules-popover"
      >
        <div className="bg-[#F0E7F4] px-4 py-3 border-b border-[#E5E7EB] rounded-t-lg">
          <div className="font-display text-sm font-bold text-[#373A45] inline-flex items-center gap-2">
            <Info size={14} className="text-[#7A4986]" />
            Reguli importante pentru planificarea grupelor
          </div>
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            Conform metodologiei-cadru (OMEC nr. 3.986/2025, art. 18). Platforma le verifică pe măsură ce
            scrii, în afară de cea marcată, și refuză salvarea unei sesiuni noi sau schimbate care nu le respectă.
          </p>
        </div>
        <div className="p-4 space-y-3 text-[12.5px] text-[#373A45]">
          <RulesGroup icon="🚫" title="Perioade interzise">
            <li>1 august – 31 august</li>
            <li>20 decembrie – 15 ianuarie</li>
            <li>vacanța școlară de Paște</li>
          </RulesGroup>
          <RulesGroup icon="📅" title="Restricții calendar">
            <li>fără cursuri duminica</li>
            <li>fără sărbători legale</li>
            <li>fără „punți"</li>
          </RulesGroup>
          <RulesGroup icon="⏰" title="Interval orar">
            <li>activitățile trebuie finalizate până la 20:00</li>
          </RulesGroup>
          <RulesGroup icon="📚" title="Limită ore">
            <li>max. 4h/zi în timpul școlii (luni–vineri)</li>
            <li>max. 8h/zi sâmbăta sau zile fără cursuri</li>
          </RulesGroup>
          <RulesGroup icon="👩‍🏫" title="Compatibilitate program">
            <li>activitățile nu se suprapun peste programul școlar (asta o verifici tu; platforma nu o poate ști)</li>
            <li>același formator nu ține două grupe în același timp</li>
          </RulesGroup>
          <RulesGroup icon="👥" title="Grupa și acreditarea">
            <li>cel mult 25 de formabili pe grupă</li>
            <li>evaluarea finală (la cel mult 30 de zile după ultima sesiune) cade în perioada acreditării</li>
          </RulesGroup>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RulesGroup({ icon, title, children }) {
  return (
    <div>
      <div className="font-semibold inline-flex items-center gap-2 mb-1 text-[#373A45]">
        <span aria-hidden="true">{icon}</span>
        {title}
      </div>
      <ul className="ml-7 list-disc space-y-0.5 text-[#4b4f5a]">{children}</ul>
    </div>
  );
}

function WarningHint({ children, testid }) {
  return (
    <div
      className="mt-1.5 inline-flex items-start gap-1.5 rounded-lg bg-[#FFF7E6] border border-[#F5C76E]/40 px-2.5 py-1.5 text-[11px] text-[#7A5A1B]"
      data-testid={testid}
    >
      <AlertTriangle size={12} className="mt-0.5 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

/**
 * Parses a free-text `"HH:MM - HH:MM"` interval (the format users actually
 * enter — we don't force a strict picker) and yields end-time + duration
 * warnings that match the MEC rules. We never block submission; this is a
 * pure guidance UI, so we silently no-op on unparseable inputs.
 */
function ScheduleTimeWarnings({ timeText, days }) {
  const parsed = parseInterval(timeText);
  if (!parsed) return null;
  const { startMin, endMin, durationHours } = parsed;
  const warnings = [];
  if (endMin > 20 * 60) {
    warnings.push("Conform metodologiei MEC, activitățile trebuie finalizate până la ora 20:00.");
  }
  if (durationHours > 0) {
    const hasWeekdayOnly = days.some((d) => ["Luni", "Marți", "Miercuri", "Joi", "Vineri"].includes(d));
    const hasWeekend = days.some((d) => ["Sâmbătă", "Duminică"].includes(d));
    if (hasWeekdayOnly && !hasWeekend && durationHours > 4) {
      warnings.push(`Durată ${durationHours.toFixed(1)}h — peste limita MEC de 4h/zi în timpul școlii.`);
    } else if (hasWeekend && !hasWeekdayOnly && durationHours > 8) {
      warnings.push(`Durată ${durationHours.toFixed(1)}h — peste limita MEC de 8h/zi în weekend.`);
    } else if (hasWeekdayOnly && hasWeekend && durationHours > 4) {
      warnings.push(`Durată ${durationHours.toFixed(1)}h — verifică limita MEC (4h școală / 8h weekend).`);
    }
  }
  if (startMin >= endMin) return null;
  if (!warnings.length) return null;
  return (
    <>
      {warnings.map((w, i) => (
        <WarningHint key={i} testid={`groups-warn-time-${i}`}>{w}</WarningHint>
      ))}
    </>
  );
}

function parseInterval(text) {
  if (!text) return null;
  const m = text.match(/(\d{1,2})[:.](\d{2})\s*[-–—]\s*(\d{1,2})[:.](\d{2})/);
  if (!m) return null;
  const startMin = parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  const endMin = parseInt(m[3], 10) * 60 + parseInt(m[4], 10);
  if (endMin <= startMin) return null;
  return { startMin, endMin, durationHours: (endMin - startMin) / 60 };
}


/**
 * Creating this cohort on Logiscool's side.
 *
 * Deliberately two presses. The FPED account holds exactly ONE trainer
 * licence, spent at the moment a trainer is assigned to a group, and there is
 * no quick way to get another. So the first button only asks what WOULD
 * happen and shows the real values — which pools, which season, which subject
 * — and the second one does it.
 *
 * That is also why the plan lists its objections rather than just disabling
 * the button: "not ready" is not useful; "the cohort has no trainer e-mail"
 * can be acted on.
 */
async function renameAtLimo(groupId, sandbox) {
  try {
    const r = await api.post(`/admin/groups/${groupId}/limo/rename`, null, { params: { sandbox } });
    if (r.data.ok) toast.success(r.data.message); else toast.error(r.data.message);
    return r.data.ok;
  } catch (err) {
    toast.error(err?.response?.data?.detail || "Redenumirea la Logiscool nu a reușit.");
    return false;
  }
}

async function syncTrainer(groupId, sandbox) {
  try {
    const r = await api.post(`/admin/groups/${groupId}/limo/sync-trainer`, null, { params: { sandbox } });
    if (!r.data.ok) { toast.error(r.data.message); return r.data; }
    const b = r.data.trainer_pool_before, a = r.data.trainer_pool_after;
    const pool = b && a && b.assigned !== a.assigned ? ` Licențe de trainer ocupate: ${b.assigned} → ${a.assigned}.` : "";
    toast.success(`${r.data.message}${pool}`);
    return r.data;
  } catch (err) {
    toast.error(err?.response?.data?.detail || "Schimbarea trainerului la Logiscool nu a reușit.");
    return null;
  }
}

function LimoGroupPanel({ groupId, group, canMutate, ensureSaved, dirty }) {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // What Logiscool actually holds, read back. A 200 from a create call means
  // they accepted the request, not that the result is what we meant.
  const [verified, setVerified] = useState(null);
  // Rehearsal against the TEST pools — a hundred licences instead of the one
  // trainer licence that exists for real. Off unless chosen, and the two
  // modes keep separate records, so a rehearsal never stands in for the real
  // run.
  // A cohort that so far exists only as a rehearsal opens in rehearsal mode,
  // so reopening it does not look as if the choice was lost (22.09.2026).
  const [sandbox, setSandbox] = useState(Boolean(group?.limo_test_group_id && !group?.limo_group_id));

  /** The panel acts on the SAVED cohort, so anything unsaved goes first. */
  const guard = async (fn) => {
    if (ensureSaved && !(await ensureSaved())) return;
    await fn();
  };

  const unlink = async () => {
    if (!window.confirm("Grupa nu va mai indica grupa Logiscool de acum. La Logiscool nu se șterge nimic. "
      + "Apoi o creezi din nou cu „Verifică ce s-ar crea” → „Creează”. Continui?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/groups/${groupId}/limo/unlink`, null, { params: { sandbox } });
      toast.success("Grupa a fost dezlegată. Creeaz-o din nou.");
      setVerified(null);
      setPlan(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nu am putut dezlega grupa.");
    } finally {
      setBusy(false);
    }
  };

  const loadPlan = async (mode = sandbox) => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.get(`/admin/groups/${groupId}/limo/plan`, {
        params: { sandbox: mode },
      });
      setPlan(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nu am putut citi configurația Logiscool.");
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/groups/${groupId}/limo/create`, null, {
        params: { sandbox },
      });
      setResult(r.data);
      if (r.data?.ok) {
        toast.success(
          sandbox
            ? "Grupă de probă creată — nicio licență reală consumată."
            : "Grupa a fost creată în Logiscool.",
        );
        await loadPlan();
      } else {
        toast.error("Creare incompletă — vezi pașii de mai jos.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Crearea a eșuat.");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/groups/${groupId}/limo/verify`, {
        params: { sandbox },
      });
      setVerified(r.data);
      if (!r.data?.ok) {
        toast.error(
          r.data?.error === "not_found_at_logiscool"
            ? "Logiscool nu recunoaște id-ul grupei pe care îl avem."
            : r.data?.error || "Verificarea a eșuat.",
        );
      }
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // A cohort already at Logiscool opens with its plan read (it sends nothing),
  // so the participants and their accounts are on the tab, not behind a
  // button (08.10.2026).
  const created = Boolean(sandbox ? group?.limo_test_group_id : group?.limo_group_id);
  useEffect(() => {
    if (created && !plan) loadPlan(sandbox);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created, sandbox]);

  const done = plan?.already?.limo_group_id;

  return (
    <div
      className="rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] p-4"
      data-testid="group-limo-panel"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold text-[#373A45]">Logiscool</span>
        <div className="flex items-center gap-1.5">
          {/* Both states, always — a cohort rehearsed in the sandbox must not
              look finished while the real run is the one still pending. */}
          {(plan?.state?.sandbox_group_id || group?.limo_test_group_id) && (
            <span className="inline-flex rounded-full bg-[#F3F4F6] text-[#6B7280] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-widest">
              probă
            </span>
          )}
          {(plan?.state?.live_group_id || group?.limo_group_id) && (
            <span className="inline-flex rounded-full bg-[#519872]/15 text-[#2F6B4E] px-2.5 py-1 text-[11px] font-semibold">
              Creată
            </span>
          )}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="group-limo-sandbox-toggle">
        <div className="inline-flex rounded-full border border-[#E5E7EB] bg-white p-0.5 text-xs font-semibold">
          {[[false, "Real"], [true, "Probă"]].map(([mode, label]) => (
            <button
              key={label}
              type="button"
              onClick={() => { setSandbox(mode); setPlan(null); setResult(null); }}
              className={`rounded-full px-3 py-1 ${
                sandbox === mode ? "bg-[#965EA7] text-white" : "text-[#6B7280] hover:text-[#373A45]"}`}
              data-testid={`group-limo-mode-${mode ? "test" : "live"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <span className="text-[11px] text-[#6B7280]" data-testid="group-limo-sandbox-hint">
          {sandbox
            ? "Proba folosește pool-urile de test și nu consumă licențe reale."
            : "Butoanele de mai jos lucrează pe pool-urile reale."}
        </span>
      </div>

      {dirty && (
        <p className="mt-2 text-[11px] text-[#8a6a1a]" data-testid="group-limo-dirty">
          Ai modificări nesalvate — butoanele de aici salvează întâi grupa, apoi lucrează pe ea.
        </p>
      )}

      {!plan && (
        <button
          type="button"
          onClick={() => guard(() => loadPlan())}
          disabled={busy}
          className="mt-2 rounded-full border border-[#E5E7EB] bg-white hover:bg-[#F5F6F6] disabled:opacity-60 text-sm font-semibold px-4 py-1.5"
          data-testid="group-limo-check"
        >
          {busy ? "Se verifică…" : "Verifică ce s-ar crea"}
        </button>
      )}

      {plan && (
        <div className="mt-3 space-y-2 text-xs text-[#373A45]">
          {done ? (
            <div className="text-[#2F6B4E]">
              {plan.sandbox ? "Grupa de probă există" : "Grupa există în Logiscool"}{" "}
              (<span className="font-mono">{done}</span>).
            </div>
          ) : plan.ready ? (
            <>
              <dl className="grid grid-cols-2 gap-2">
                <LimoPlanField label="Trainer" value={`${plan.trainer_name} · ${plan.trainer_email}`} />
                <LimoPlanField label="Sezon" value={plan.season?.code} />
                <LimoPlanField label="Pool trainer" value={plan.trainer_pool?.name} />
                <LimoPlanField label="Pool cursanți" value={plan.student_pool?.name} />
              </dl>
              {plan.sandbox ? (
                <p className="text-[#2F6B4E] bg-[#519872]/10 border border-[#519872]/30 rounded-lg px-3 py-2 leading-relaxed">
                  Rulare de probă, în pool-urile TEST. Nu se atinge nicio
                  licență reală, iar rezultatul se ține separat — grupa va
                  trebui creată din nou, fără bifă, când e de-adevăratelea.
                </p>
              ) : (
                <p className="text-[#8a6a1a] bg-[#F59E0B]/10 border border-[#F59E0B]/40 rounded-lg px-3 py-2 leading-relaxed">
                  Grupa se creează pe contul Logiscool afișat la „Trainer”. O licență ține un singur
                  formator odată, pentru toate grupele lui; conturile le gestionezi din fila Traineri.
                </p>
              )}
              {canMutate && (
                <button
                  type="button"
                  onClick={() => guard(create)}
                  disabled={busy}
                  className={`rounded-full disabled:opacity-60 text-white text-sm font-semibold px-4 py-1.5 ${
                    plan.sandbox
                      ? "bg-[#519872] hover:bg-[#3f7a5c]"
                      : "bg-[#965EA7] hover:bg-[#7A4986]"
                  }`}
                  data-testid="group-limo-create"
                >
                  {busy ? "Se creează…" : plan.sandbox ? "Creează de probă" : "Creează în Logiscool"}
                </button>
              )}
            </>
          ) : (
            <ul className="space-y-1 text-[#9b1f2c]" data-testid="group-limo-problems">
              {plan.problems.map((pr) => (
                <li key={pr}>• {pr}</li>
              ))}
            </ul>
          )}

          {done && (
            <div className="pt-1">
              <button
                type="button"
                onClick={() => guard(verify)}
                disabled={busy}
                className="rounded-full border border-[#3F8EBA]/40 text-[#2B6285] hover:bg-[#EAF2F8] disabled:opacity-60 text-xs font-semibold px-3 py-1.5"
                data-testid="group-limo-verify"
              >
                {busy ? "Se verifică…" : "Vezi ce are Logiscool"}
              </button>
              <span className="ml-2 text-[11px] text-[#6B7280]">
                citește grupa înapoi de la ei — nu e nevoie de cont MyLogiscool
              </span>
            </div>
          )}

          {verified?.ok && (
            <div
              className="rounded-xl border border-[#E5E7EB] bg-white p-3"
              data-testid="group-limo-verified"
            >
              <dl className="grid grid-cols-2 gap-2">
                <LimoPlanField label="Nume la ei" value={verified.name} />
                <LimoPlanField label="Tip" value={verified.type} />
                <LimoPlanField
                  label="Trainer atașat"
                  value={
                    verified.trainers.length
                      ? verified.trainers.map((t) => t.name || t.id).join(", ")
                      : "NICIUNUL"
                  }
                />
                <LimoPlanField
                  label="Cursanți"
                  value={verified.member_count ?? "—"}
                />
              </dl>
              {/* Stated rather than left for somebody to spot among the
                  fields — a renamed cohort here does not rename it there. */}
              {!verified.name_matches && (
                <div className="mt-2 text-xs text-[#8a6a1a]">
                  Numele diferă de cel din platforma noastră.
                  {canMutate && (
                    <button type="button" disabled={busy}
                      onClick={() => guard(async () => { setBusy(true); const ok = await renameAtLimo(groupId, sandbox); setBusy(false); if (ok) setVerified(null); })}
                      className="ml-2 rounded-full border border-[#8a6a1a]/40 bg-white px-3 py-0.5 font-semibold hover:bg-[#FDF3E7] disabled:opacity-60"
                      data-testid="group-limo-rename">
                      Trimite numele la Logiscool
                    </button>
                  )}
                </div>
              )}
              {!verified.has_trainer && (
                <p className="mt-2 text-xs text-[#9b1f2c]">
                  Niciun trainer atașat la ei, deși atribuirea a raportat succes.
                </p>
              )}
              {verified.trainer_matches === false && (
                <div className="mt-2 rounded-lg bg-[#FDF3E7] px-3 py-2 text-xs text-[#8a6a1a]" data-testid="group-limo-trainer-mismatch">
                  <p>La Logiscool, grupa are alt trainer decât cel ales aici.</p>
                  {canMutate && (
                    <button type="button" disabled={busy}
                      onClick={() => guard(async () => {
                        if (!window.confirm("Pun la Logiscool trainerul ales aici? Cel vechi e scos din grupă; dacă Logiscool refuză, e pus la loc.")) return;
                        setBusy(true);
                        const out = await syncTrainer(groupId, sandbox);
                        setBusy(false);
                        if (out?.ok) setVerified(null);
                      })}
                      className="mt-1.5 rounded-full border border-[#8a6a1a]/40 bg-white px-3 py-1 font-semibold hover:bg-[#FDF3E7] disabled:opacity-60"
                      data-testid="group-limo-sync-trainer">
                      Actualizează trainerul la Logiscool
                    </button>
                  )}
                </div>
              )}
              {verified.shared_with?.length > 0 && (
                <div className="mt-2 rounded-lg bg-[#FDECEC] px-3 py-2 text-xs text-[#9b1f2c]" data-testid="group-limo-shared">
                  <p>
                    Aceeași grupă Logiscool e folosită și de: <strong>{verified.shared_with.join(", ")}</strong>.
                    Una dintre ele a primit la creare id-ul celeilalte.
                  </p>
                  {canMutate && (
                    <button type="button" onClick={() => guard(unlink)} disabled={busy}
                      className="mt-1.5 rounded-full border border-[#9b1f2c]/40 bg-white px-3 py-1 font-semibold hover:bg-[#FDECEC] disabled:opacity-60"
                      data-testid="group-limo-unlink">
                      Dezleagă această grupă, ca s-o creez din nou
                    </button>
                  )}
                </div>
              )}
              {verified.name_matches && verified.has_trainer && !verified.shared_with?.length && verified.trainer_matches !== false && (
                <p className="mt-2 text-xs text-[#2F6B4E]">
                  Totul se potrivește.
                </p>
              )}
            </div>
          )}

          {done && (
            <LimoGroupParticipants groupId={groupId} sandbox={plan.sandbox} canMutate={canMutate} />
          )}

          {result?.steps?.length > 0 && (
            <ul className="mt-2 space-y-0.5 font-mono text-[11px]" data-testid="group-limo-steps">
              {result.steps.map((st, i) => (
                <li key={i} className={st.ok ? "text-[#2F6B4E]" : "text-[#9b1f2c]"}>
                  {st.ok ? "✓" : "✗"} {st.step}
                  {!st.ok && st.data?.error ? ` — ${st.data.error}` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function LimoPlanField({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-widest text-[#6B7280] font-semibold">{label}</dt>
      <dd className="mt-0.5 text-[#373A45]">{value || "—"}</dd>
    </div>
  );
}

/**
 * Pick the person who teaches this cohort.
 *
 * Only trainers that exist are offered — there is no "type a name here",
 * because a name typed in this box is what used to become a separate
 * Logiscool record per cohort. Somebody new is added in the Traineri tab,
 * once, and then appears everywhere.
 */
/**
 * Which Logiscool account the cohort teaches on. The one its trainer already
 * holds is proposed, because a licence covers every cohort of its holder;
 * an account held by somebody else is shown as taken, not offered.
 */
/**
 * The licence this cohort teaches on (24.09.2026).
 *
 * A licence is annual and belongs to the account, so the normal case is the
 * one the chosen formator already holds — said in words rather than left to
 * be read out of a dropdown. When they hold none, the seat can be handed to
 * them right here: walking to the Traineri tab to do it was the reason this
 * step got forgotten.
 */
function AccountPicker({ value, trainerId, trainerName, onChange }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    let live = true;
    api.get("/admin/trainer-accounts")
      .then((r) => { if (live) setRows(r.data || []); })
      .catch(() => { if (live) setRows([]); });
    return () => { live = false; };
  }, []);

  useEffect(() => load(), [load]);

  const held = (rows || []).find((a) => a.holder?.trainer_id === trainerId);
  const effective = value || held?.id || "";
  const chosen = (rows || []).find((a) => a.id === effective);
  const since = held?.holder?.since ? shortDate(held.holder.since) : "";

  const give = async () => {
    if (!chosen || !trainerId) return;
    setBusy(true);
    try {
      await api.post(`/admin/trainer-accounts/${chosen.id}/hold`,
        { trainer_id: trainerId, reset_password: false });
      toast.success(`Licența „${chosen.label}” e acum la ${trainerName || "formatorul ales"}.`);
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu am putut da licența."));
    } finally {
      setBusy(false);
    }
  };

  if (rows === null) return <div className="text-xs text-[#6B7280]">Se încarcă licențele…</div>;
  if (rows.length === 0) {
    return (
      <div className="text-xs text-[#8a6a1a]">
        Nicio licență Logiscool definită. Creează-le în tabul Traineri, apoi alege una aici.
      </div>
    );
  }

  return (
    <div>
      <select
        value={effective}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-3 py-2 text-sm"
        data-testid="group-limo-account"
      >
        <option value="">— alege licența —</option>
        {rows.map((a) => (
          <option key={a.id} value={a.id}>
            {a.label}
            {a.holder ? ` · ${a.holder.name}` : " · liberă"}
            {a.limo_trainer_id ? "" : " · necreată în Logiscool"}
          </option>
        ))}
      </select>
      {held && held.id === effective ? (
        <p className="mt-1 text-[11px] text-[#2F6B4E]" data-testid="group-account-hint">
          {trainerName || "Formatorul ales"} ține licența „{held.label}”{since ? ` din ${since}` : ""} — e
          propusă automat și acoperă toate grupele lui.
        </p>
      ) : (
        <div className="mt-1 text-[11px] text-[#6B7280]" data-testid="group-account-hint">
          {!trainerId
            ? "Alege întâi formatorul; licența lui se propune singură."
            : held
              ? `Atenție: ${trainerName || "formatorul ales"} ține „${held.label}”, nu licența aleasă aici.`
              : `${trainerName || "Formatorul ales"} nu ține nicio licență.`}
          {trainerId && !held && chosen && !chosen.holder && (
            <button
              type="button"
              onClick={give}
              disabled={busy}
              className="ml-2 rounded-full border border-[#E5E7EB] bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#373A45] hover:bg-[#F5F6F6] disabled:opacity-60"
              data-testid="group-account-give"
            >
              {busy ? "Se dă…" : `Dă-i „${chosen.label}”`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}


function TrainerPicker({ rows, value, onChange }) {
  if (rows === null || rows === undefined) {
    return <div className="text-xs text-[#6B7280] py-2">Se încarcă trainerii…</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="text-xs text-[#8a6a1a] bg-[#F59E0B]/10 border border-[#F59E0B]/40 rounded-xl px-3 py-2">
        Niciun trainer definit. Adaugă unul din tabul <strong>Traineri</strong>.
      </div>
    );
  }
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-3 py-2 text-sm"
      data-testid="group-trainer-picker"
    >
      <option value="">— fără trainer —</option>
      {rows.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name} · {t.email}
        </option>
      ))}
    </select>
  );
}
